'use client'

import { useEffect, useId } from 'react'

import confetti from 'canvas-confetti'

import { LevelCosmeticEntry } from '../types'

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'
import {
  Dialog,
  DialogContent,
  DialogTitle
} from '@/shared/ui/dialog'

function fireUnlockConfetti() {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
    return

  const duration = 2.8 * 1000
  const animationEnd = Date.now() + duration
  const defaults = {
    startVelocity: 32,
    spread: 360,
    ticks: 55,
    zIndex: 9999,
    colors: ['#FFE400', '#FFBD00', '#E89400', '#a25afd', '#26ccff', '#88ff5a', '#ff5e7e']
  }
  const randomInRange = (min: number, max: number) =>
    Math.random() * (max - min) + min

  const interval = window.setInterval(() => {
    const timeLeft = animationEnd - Date.now()
    if (timeLeft <= 0) {
      window.clearInterval(interval)
      return
    }
    const particleCount = 42 * (timeLeft / duration)
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.12, 0.32), y: randomInRange(0.15, 0.45) } })
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.68, 0.88), y: randomInRange(0.15, 0.45) } })
  }, 220)

  const starDefaults = {
    spread: 360, ticks: 48, gravity: 0.45, decay: 0.94, startVelocity: 28,
    colors: ['#FFE400', '#FFBD00', '#FDFFB8', '#fcff42'], zIndex: 9999
  }
  confetti({ ...starDefaults, particleCount: 45, scalar: 1.1, shapes: ['star'], origin: { x: 0.5, y: 0.42 } })
  window.setTimeout(() => {
    confetti({ ...starDefaults, particleCount: 28, scalar: 0.85, shapes: ['circle'], origin: { x: 0.5, y: 0.38 } })
  }, 120)
}

interface CosmeticUnlockModalProps {
  cosmetic: LevelCosmeticEntry
  onConfirm: () => void
}

export function CosmeticUnlockModal({
  cosmetic,
  onConfirm
}: CosmeticUnlockModalProps) {
  const titleId = useId()
  const descId = useId()

  useEffect(() => {
    fireUnlockConfetti()
  }, [])

  return (
    <Dialog open>
      <DialogContent
        hideCloseButton
        onInteractOutside={e => e.preventDefault()}
        className={cn(
          'p-0 overflow-hidden border-2 border-[#f4d03f]/90',
          'bg-[linear-gradient(180deg,#2d1b3d_0%,#1f1228_48%,#160d1f_100%)]',
          'shadow-[0_0_0_1px_rgba(255,215,80,0.35),0_12px_40px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.12)]',
          'sm:max-w-sm'
        )}
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14]"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg,transparent,transparent 2px,rgba(255,255,255,0.03) 2px,rgba(255,255,255,0.03) 4px)'
          }}
        />

        <DialogTitle className="sr-only" id={titleId}>
          Avatar Desbloqueado
        </DialogTitle>

        <div className="relative px-5 pb-5 pt-6 flex flex-col items-center gap-4">
          <span className="inline-block rounded-full border border-amber-400/50 bg-amber-400/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-200/95">
            Recompensa
          </span>

          <h2
            id={titleId}
            className="text-center font-sans text-xl font-extrabold uppercase tracking-wide text-amber-100 drop-shadow-[0_1px_0_rgba(0,0,0,0.5)]"
          >
            Avatar Desbloqueado
          </h2>

          <p
            id={descId}
            className="text-center text-sm font-medium text-violet-100/75"
          >
            Você desbloqueou <b className="text-amber-200">{cosmetic.name}</b> ao concluir este nível!
          </p>

          <div className="w-full max-w-[200px]">
            {cosmetic.image !== null ? (
              <div className="mx-auto aspect-square w-full overflow-hidden rounded-2xl ring-2 ring-amber-400/35 shadow-[0_0_32px_-8px_rgba(250,204,21,0.45)]">
                <img
                  src={cosmetic.image.file.url}
                  alt={cosmetic.name}
                  className="size-full object-cover"
                />
              </div>
            ) : (
              <p className="text-center text-base font-semibold text-violet-100/60">
                {cosmetic.name}
              </p>
            )}
          </div>

          <Button
            type="button"
            variant="reward"
            className="w-full mt-1"
            onClick={onConfirm}
          >
            Confirmar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
